import { FamilyApplicationEndpoints } from '@/apis/family_application/requests'
import axiosInstance from '@/libs/axios_config'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { useMutation, UseMutationOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useSelector } from 'react-redux'
import {
  DocumentTypeValues,
  DocumentVariantValues,
} from './document-upload-new.constants'

interface UploadResponseOCRContent {
  errors?: []
}

interface UploadResponseOCR {
  residency_validated: boolean
  ocr_response?: UploadResponseOCRContent
}

interface UploadResponse {
  success: boolean
  message: string
  document_id?: string
  ocr?: UploadResponseOCR
}

interface UploadError {
  message: string
  code?: string
}

export type DocumentUploadOptions = Omit<
  UseMutationOptions<UploadResponse, AxiosError<UploadError>, File>,
  'mutationFn'
> & {
  variation: DocumentVariantValues
  type: DocumentTypeValues
  referer_page?: 'manual_identity_verification' | 'manual_residency_verification'
}

function useDocumentUpload({
  variation,
  type,
  referer_page,
  ...rest
}: DocumentUploadOptions) {
  const programId = useSelector(selectProgramID)

  const mutation = useMutation<UploadResponse, AxiosError<UploadError>, File>({
    mutationFn: uploadDocument,
    ...rest,
  })

  async function uploadDocument(image: File) {
    const formData = new FormData()
    formData.append('file', image)
    formData.append('type', String(type))
    formData.append('variation', String(variation))
    formData.append('program_id', String(programId))
    if (referer_page) {
      formData.append('referer_page', referer_page)
    }

    const response = await axiosInstance.post<UploadResponse>(
      FamilyApplicationEndpoints.post_residency_document,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    return response.data
  }

  return mutation
}

export default useDocumentUpload
